// Merging two accounts that are the same person (e.g. one made with a work address, one with a personal
// one). The Users page points out accounts that look alike (DuplicateNotice); the dialog picks which one
// to keep, says what happens, merges (worker db/aliases.ts), then says what moved over. The rules live in
// packages/shared userMerge.ts.
import { useMemo, useState } from 'react';
import { describeMerge, likelyDuplicates, likelySamePerson, suggestedKeeper, type MergeSummary } from '@technohull/shared';
import { useMergeUsers, type AdminUser } from '../../features/admin.js';
import { InlineError, Modal } from '../../components/ui.js';

const person = (u: AdminUser) => ({ email: u.email, displayName: u.display_name, lastSeenAt: u.last_seen_at ?? null });
const nameOf = (u: AdminUser) => u.display_name || u.email.split('@')[0]!;

// pairs an admin said are different people, kept on this device so the notice doesn't keep asking
const NOT_DUPES_KEY = 'rolodex.notDuplicates';
const pairKey = (a: string, b: string) => [a, b].sort().join('|');
function readNotDupes(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(NOT_DUPES_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

/** "these look like the same person" above the users table, with Merge and "Not the same person". */
export function DuplicateNotice({ users, onMerge }: { users: AdminUser[]; onMerge: (source: string, target: string) => void }) {
  const [dismissed, setDismissed] = useState(readNotDupes);
  const pairs = useMemo(() => {
    const byEmail = new Map(users.map((u) => [u.email, u]));
    return likelyDuplicates(users.map(person))
      .map(([a, b]) => [byEmail.get(a.email)!, byEmail.get(b.email)!] as const)
      .filter(([a, b]) => !dismissed.has(pairKey(a.email, b.email)));
  }, [users, dismissed]);
  if (!pairs.length) return null;
  const dismiss = (a: string, b: string) => {
    const next = new Set(dismissed).add(pairKey(a, b));
    setDismissed(next);
    try { localStorage.setItem(NOT_DUPES_KEY, JSON.stringify([...next])); } catch { /* this visit only */ }
  };
  return (
    <div className="page-panel u-dupes" role="region" aria-label="Possible duplicate accounts">
      <h2 className="adm-panel-title u-dupes-hd">Possible duplicate accounts</h2>
      <p className="muted u-dupes-hint">These look like the same person signed in with two addresses. Merge them into one account, or say they're different people.</p>
      <ul className="u-dupes-list">
        {pairs.map(([a, b]) => {
          const keep = suggestedKeeper(person(a), person(b)).email === a.email ? a : b;
          const away = keep === a ? b : a;
          return (
            <li key={pairKey(a.email, b.email)} className="u-dupe">
              <span className="u-dupe-who">
                <b>{nameOf(a)}</b> <span className="muted">{a.email}</span>
                <span className="u-dupe-and">and</span>
                <b>{nameOf(b)}</b> <span className="muted">{b.email}</span>
              </span>
              <span className="u-dupe-acts">
                <button className="btn btn-sm btn-primary" onClick={() => onMerge(away.email, keep.email)}>Merge…</button>
                <button className="btn btn-sm btn-ghost" onClick={() => dismiss(a.email, b.email)}>Not the same person</button>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** pick the account to keep and the one to merge into it, see what happens, merge, see what moved. */
export function MergeDialog({ users, me, initial, rolesText, roleLabel, onClose }: {
  users: AdminUser[];
  me: string | undefined;
  initial: { source: string; target: string };
  /** a person's roles as the Users list reads them (same order) */
  rolesText: (u: AdminUser) => string;
  /** a role's name as the Users list shows it */
  roleLabel: (role: string) => string;
  onClose: () => void;
}) {
  const merge = useMergeUsers();
  const [source, setSource] = useState(initial.source);
  const [target, setTarget] = useState(initial.target);
  // once merged: what moved, with the two accounts as they were (the merged-away one leaves the list)
  const [done, setDone] = useState<{ summary: MergeSummary; kept: AdminUser; away: AdminUser } | null>(null);
  const byEmail = useMemo(() => new Map(users.map((u) => [u.email, u])), [users]);
  const s = byEmail.get(source);
  const t = byEmail.get(target);
  // the other account to pick: the likely matches first
  const options = useMemo(() => {
    const others = (anchor?: AdminUser) => {
      const list = users.filter((u) => u.email !== anchor?.email);
      const likely = anchor ? list.filter((u) => likelySamePerson(person(anchor), person(u))) : [];
      const rest = list.filter((u) => !likely.includes(u));
      return { likely, rest };
    };
    return { forTarget: others(s), forSource: others(t) };
  }, [users, s, t]);

  const sameAsMe = !!me && source === me;
  const ready = !!s && !!t && source !== target && !sameAsMe;
  const swap = () => { setSource(target); setTarget(source); merge.reset(); };

  const select = (value: string, onChange: (v: string) => void, opts: { likely: AdminUser[]; rest: AdminUser[] }, label: string) => (
    <select className="field" aria-label={label} value={value} onChange={(e) => { onChange(e.target.value); merge.reset(); }}>
      <option value="" disabled>Choose an account…</option>
      {opts.likely.length > 0 && (
        <optgroup label="Likely the same person">
          {opts.likely.map((u) => <option key={u.email} value={u.email}>{nameOf(u)} · {u.email}</option>)}
        </optgroup>
      )}
      <optgroup label={opts.likely.length ? 'Everyone else' : 'Accounts'}>
        {opts.rest.map((u) => <option key={u.email} value={u.email}>{nameOf(u)} · {u.email}</option>)}
      </optgroup>
    </select>
  );

  if (done) {
    return (
      <Modal title="Accounts merged" width={600} className="u-merge" onClose={onClose} footer={<button className="btn btn-primary" onClick={onClose}>Done</button>}>
        <MergeResult {...done} roleLabel={roleLabel} />
      </Modal>
    );
  }

  return (
    <Modal
      title="Merge accounts"
      width={600}
      className="u-merge"
      onClose={onClose}
      footer={(
        <>
          <button className="btn btn-ghost" disabled={merge.isPending} onClick={onClose}>Cancel</button>
          <button
            className="btn btn-danger"
            disabled={!ready || merge.isPending}
            onClick={() => merge.mutate({ source, target }, { onSuccess: (r) => setDone({ summary: r.summary, kept: t!, away: s! }) })}
          >
            {merge.isPending ? 'Merging…' : 'Merge'}
          </button>
        </>
      )}
    >
      <p className="muted" style={{ marginTop: 0 }}>For one person with two accounts, for example one made with a work address and one with a personal one.</p>
      <div className="u-merge-pair">
        <div className="u-merge-side keep">
          <span className="u-merge-lbl">Keep</span>
          {select(target, setTarget, options.forTarget, 'Account to keep')}
          {t && <span className="muted u-merge-roles">{t.email}<br />{rolesText(t)}</span>}
        </div>
        <button type="button" className="btn btn-sm u-merge-swap" onClick={swap} title="Keep the other one instead" aria-label="Swap which account to keep">⇄</button>
        <div className="u-merge-side away">
          <span className="u-merge-lbl">Merge into it and remove</span>
          {select(source, setSource, options.forSource, 'Account to merge away')}
          {s && <span className="muted u-merge-roles">{s.email}<br />{rolesText(s)}</span>}
        </div>
      </div>
      {s && t && source !== target && (
        <ul className="u-merge-what">
          <li>
            <b>{nameOf(s)}</b> ({s.email}) is removed. Everything it had moves to <b>{nameOf(t)}</b>: roles, departments, chats
            (one conversation per person, its messages shown as {nameOf(t)}'s), agenda, pins, orders, saved filters and notifications.
          </li>
          <li>Signing in as {s.email}, with Google or its password, opens {nameOf(t)}'s account.</li>
          <li>
            {nameOf(t)} keeps their own name, address and settings; a setting they haven't chosen comes from {nameOf(s)}
            {!t.has_password && s.has_password ? `, and so does the password (they had none)` : ''}.
          </li>
          <li>Past changes made as {s.email} stay in the history, shown under {nameOf(t)}'s name.</li>
        </ul>
      )}
      {sameAsMe && <InlineError error={new Error("You can't merge away the account you're signed in with. Keep it, and merge the other one into it.")} />}
      {merge.isError && <InlineError error={merge.error} onDismiss={() => merge.reset()} />}
    </Modal>
  );
}

/** after a merge: who became part of whom, and what moved over, in plain words. */
function MergeResult({ summary, kept, away, roleLabel }: { summary: MergeSummary; kept: AdminUser; away: AdminUser; roleLabel: (role: string) => string }) {
  const lines = describeMerge(summary, { kept: nameOf(kept), keptEmail: kept.email, away: nameOf(away), awayEmail: away.email }, roleLabel);
  return (
    <div role="status">
      <p className="u-merge-lead">
        <b>{nameOf(away)}</b> ({away.email}) is now part of <b>{nameOf(kept)}</b>. Signing in as {away.email} opens {nameOf(kept)}'s account.
      </p>
      <span className="u-merge-lbl">What moved over</span>
      {lines.length > 0 ? (
        <ul className="u-merge-what u-merge-moved">
          {lines.map((l) => <li key={l}>{l}</li>)}
        </ul>
      ) : (
        <p className="muted u-merge-moved">Nothing else to move: {nameOf(away)} had nothing of its own here.</p>
      )}
    </div>
  );
}
